import { useCallback, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Badge,
  Button,
  Flex,
  Spinner,
  Text,
  Table,
  Box,
  Em,
} from "@radix-ui/themes";
import { DotsVerticalIcon } from "@radix-ui/react-icons";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import { useQuackchatPlatformConfiguration } from "@/api/quackchat/configuration/useQuackchatPlatformConfiguration";
import { AutomationConfigurationContentHeader } from "../parts/AutomationConfigurationContentHeader";
import styles from "../AutomationConfiguration.module.scss";

import { useEscalationRuleApi } from "@/v2/entities/escalation-rule/EscalationRule.api";
import { useQuery, useMutation } from "react-query";
import { toast } from "sonner";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";
import { useIntegrations } from "@/api/integration/Integration.api";
import { isIntegrationCompleted } from "@/api/integration/Integration.model";

export function QuackChatChannelEscalationConfiguration() {
  const navigate = useNavigate();
  const { simulatorId, ruleId } = useParams();
  const { workspace } = useWorkspace();
  const {
    data: configuration,
    isLoading: isConfigurationLoading,
    updateStatus,
    update: updateConfiguration,
  } = useQuackchatPlatformConfiguration(simulatorId);

  const { getEscalationRules, deleteEscalationRule, getLiveAgentAvailability } =
    useEscalationRuleApi(simulatorId);

  const {
    data: escalationRules,
    isLoading: isEscalationRulesLoading,
    refetch,
  } = useQuery({
    queryKey: ["escalation-rules", simulatorId],
    queryFn: getEscalationRules,
  });

  const { data: integrations } = useIntegrations();

  const isZendeskConnected = useMemo(
    () =>
      integrations?.some(
        (i) =>
          i.name === "zendesk" &&
          i.type === "TICKET_SYSTEM" &&
          isIntegrationCompleted(i)
      ),
    [integrations]
  );

  const isSmoochConnected = useMemo(
    () =>
      integrations?.some(
        (i) =>
          i.name === "smooch" && i.type === "CHAT" && isIntegrationCompleted(i)
      ),
    [integrations]
  );

  const deleteMutation = useMutation({
    mutationFn: deleteEscalationRule,
    onSuccess: () => {
      toast.success("Escalation rule deleted successfully");
      refetch();
    },
    onError: (error) => {
      console.error("Failed to delete escalation rule:", error);
      toast.error("Failed to delete escalation rule");
    },
  });

  const handleDeleteRule = useCallback(
    (ruleId: string) => {
      if (
        window.confirm("Are you sure you want to delete this escalation rule?")
      ) {
        deleteMutation.mutate(ruleId);
      }
    },
    [deleteMutation]
  );

  if (isConfigurationLoading || !configuration) {
    return <Spinner />;
  }

  return (
    <Flex direction={"column"} gap={"24px"}>
      <AutomationConfigurationContentHeader
        title={"Escalations"}
        status={updateStatus}
      />

      <Flex direction={"column"} gap={"24px"}>
        {/* Live Agent Transfer */}
        <Flex
          gap={"2"}
          direction={"column"}
          className={styles.FieldGroupWrapper}
        >
          <Box className={styles.FieldGroup}>
            <Flex justify={"between"} gap={"3"}>
              <Flex direction={"column"} gap={"1"}>
                <Text size={"2"} as={"label"}>
                  Live Agent Escalation
                </Text>
                <Text size={"2"} color={"gray"}>
                  <Em>Allow escalation to a live agent</Em>
                </Text>
              </Flex>
            </Flex>
          </Box>
          <Flex direction={"column"} gap={"16px"}>
            {isZendeskConnected && isSmoochConnected ? (
              <>
                <Badge
                  size="2"
                  variant="soft"
                  color="green"
                  style={{
                    display: "inline-flex",
                    width: "fit-content",
                    whiteSpace: "nowrap",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    style={{ marginRight: 6, verticalAlign: "middle" }}
                  >
                    <path
                      d="M4 8.5L7 11.5L12 6.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Live Support Connected
                </Badge>
              </>
            ) : isZendeskConnected && !isSmoochConnected ? (
              <>
                <Text size={"2"} color={"gray"}>
                  Escalation to live agents will be available once you connect
                  your Smooch integration.
                </Text>
                <Button
                  style={{ width: "fit-content" }}
                  color="gray"
                  variant="outline"
                  highContrast
                  type="button"
                  onClick={() => navigate("/settings/data-sources")}
                >
                  Connect Live Support
                </Button>
              </>
            ) : (
              <>
                <Text size={"2"} color={"gray"}>
                  Escalation to live agent will be available once you connect
                  your support team. Contact us at{" "}
                  <a
                    href="mailto:Support@quack.ai"
                    className={styles.SupportEmailLink}
                  >
                    Support@quack.ai
                  </a>{" "}
                  for assistance.
                </Text>
              </>
            )}
          </Flex>
        </Flex>

        {/* Escalation Rules */}
        <Flex
          gap={"2"}
          direction={"column"}
          className={styles.FieldGroupWrapper}
        >
          <Box className={styles.FieldGroup}>
            <Flex justify={"between"} gap={"3"}>
              <Flex direction={"column"} gap={"1"}>
                <Text size={"2"} as={"label"}>
                  Escalation Rules
                </Text>
                <Text size={"2"} color={"gray"}>
                  <Em>Configure when and how to escalate conversations</Em>
                </Text>
              </Flex>
              <Flex flexShrink={"0"} pr={"1"}>
                <Button
                  variant="solid"
                  size="2"
                  highContrast
                  onClick={() =>
                    navigate(
                      `/automation/${simulatorId}/escalation-rules/create`
                    )
                  }
                >
                  + Add Escalation Rule
                </Button>
              </Flex>
            </Flex>
          </Box>
          <Flex direction={"column"} gap={"16px"}>
            {isEscalationRulesLoading ? (
              <Flex justify="center" align="center" py="4">
                <Spinner />
              </Flex>
            ) : (
              <Table.Root style={{ width: "100%" }}>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell style={{ width: "40%" }}>
                      Name
                    </Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell
                      style={{ width: "30%", textAlign: "right" }}
                    ></Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {escalationRules?.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={3} style={{ textAlign: "center" }}>
                        <Text size="2" color="gray">
                          No escalation rules found. Create your first rule to
                          get started.
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    escalationRules?.map((rule) => (
                      <Table.Row key={rule.id}>
                        <Table.Cell style={{ width: "40%" }}>
                          <Flex align="center" gap="2">
                            <Text weight="medium" highContrast>
                              {rule.name}
                            </Text>
                          </Flex>
                        </Table.Cell>
                        <Table.Cell
                          style={{ width: "60%", textAlign: "right" }}
                        >
                          <Flex gap="2" justify="end" align="center" wrap="nowrap">
                            <Flex gap="2" justify="end" align="center" wrap="wrap">
                              {rule.name === "Default Rule" ? (
                                <Badge color="blue" variant="soft">
                                  Default
                                </Badge>
                              ) : rule.topics && rule.topics.length > 0 ? (
                                rule.topics.map((topic) => (
                                  <Badge color="yellow" variant="soft">
                                    {topic}
                                  </Badge>
                                ))
                              ) : (
                                <></>
                              )}
                            </Flex>
                            <DropdownMenu
                              items={[
                                {
                                  label: "Edit",
                                  onClick: () => {
                                    navigate(
                                      `/automation/${simulatorId}/escalation-rules/edit/${rule.id}`
                                    );
                                  },
                                },
                                ...(rule.name !== "Default Rule"
                                  ? [
                                      {
                                        label: deleteMutation.isLoading
                                          ? "Deleting..."
                                          : "Delete",
                                        onClick: () =>
                                          handleDeleteRule(rule.id),
                                      },
                                    ]
                                  : []),
                              ]}
                              align="end"
                            >
                              <DropdownMenu.Trigger>
                                <Button
                                  size="1"
                                  variant="ghost"
                                  color="gray"
                                  disabled={deleteMutation.isLoading}
                                >
                                  <DotsVerticalIcon />
                                </Button>
                              </DropdownMenu.Trigger>
                            </DropdownMenu>
                          </Flex>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table.Root>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
